import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { MaskedLine, EASE } from "@/components/Reveal";
import LenderCollage from "@/components/home/LenderCollage";
import { COMPANY } from "@/data/content";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 100]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [4, -4]), { stiffness: 140, damping: 20 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-4, 4]), { stiffness: 140, damping: 20 });

  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden pt-32 sm:pt-40 pb-16 sm:pb-24">
      <div className="absolute -top-40 -right-40 w-[36rem] h-[36rem] rounded-full bg-gold/10 blur-3xl pointer-events-none" />
      <div className="absolute top-64 -left-48 w-[28rem] h-[28rem] rounded-full bg-brand/10 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-14 lg:gap-10 items-center">
          <div className="lg:col-span-7">
            <motion.img
              src={COMPANY.logo}
              alt="Invesmo — Debt Capital Investment and Development Advisory"
              data-testid="hero-logo"
              initial={{ opacity: 0, y: 24, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 1, ease: EASE }}
              className="w-72 sm:w-96 lg:w-[28rem] h-auto mb-10"
            />

            <h1 className="font-display font-extrabold tracking-tight leading-[1.04] text-4xl sm:text-5xl lg:text-6xl text-ink">
              <MaskedLine delay={0.15}>Commercial property &</MaskedLine>
              <MaskedLine delay={0.3}>specialist practice</MaskedLine>
              <MaskedLine delay={0.45}>
                <span className="font-editorial italic font-medium text-transparent bg-clip-text bg-gradient-to-r from-gold-500 to-brand">
                  finance.
                </span>
              </MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
              className="mt-8 text-base sm:text-lg text-ink/60 max-w-xl leading-relaxed"
            >
              Structured around your ambition. Invesmo is a commercial mortgage broker in Melbourne arranging
              commercial property, development and practice finance for doctors, accountants, professionals and
              investors — with access to 100+ lenders through the LMG Broker Services panel.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/book"
                data-testid="hero-book-cta"
                className="group rounded-full bg-obsidian text-gold px-8 py-4 text-sm font-semibold tracking-wide inline-flex items-center gap-3 transition-all duration-300 hover:bg-gold hover:text-obsidian hover:shadow-gold-glow"
              >
                Book a meeting
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-services-cta"
                className="rounded-full border border-ink/15 px-8 py-4 text-sm font-semibold tracking-wide text-ink transition-all duration-300 hover:border-gold hover:text-gold-700"
              >
                Explore services
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.05 }}
              className="mt-10 flex items-center gap-2 text-xs text-ink/45"
            >
              <ShieldCheck size={14} className="text-brand shrink-0" />
              <span>
                Credit Representative {COMPANY.crn} of {COMPANY.aggregator} · ACL {COMPANY.acl}
              </span>
            </motion.div>
          </div>

          <div className="lg:col-span-5" style={{ perspective: 1200 }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
              onMouseMove={onMouseMove}
              onMouseLeave={() => {
                mx.set(0);
                my.set(0);
              }}
              style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
              className="relative"
            >
              <div className="relative rounded-[2rem] overflow-hidden shadow-lift aspect-[4/5] ring-1 ring-ink/5">
                <motion.div style={{ y: imgY, scale: 1.08 }} className="h-full w-full">
                  <LenderCollage />
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.1, ease: EASE }}
                style={{ transform: "translateZ(50px)" }}
                className="absolute -bottom-6 -left-4 sm:-left-8 glass rounded-2xl px-6 py-5 shadow-lift"
                data-testid="hero-stat-card"
              >
                <div className="font-display text-3xl font-bold text-ink">100+</div>
                <div className="text-xs text-ink/55 mt-1 tracking-wide">lenders on the LMG panel</div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: -16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.25, ease: EASE }}
                style={{ transform: "translateZ(40px)" }}
                className="absolute -top-4 right-6 rounded-full bg-obsidian text-gold text-xs font-semibold tracking-widest uppercase px-5 py-2.5 shadow-lift"
              >
                Camberwell · VIC
              </motion.div>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.4 }}
              className="mt-10 text-center text-[11px] text-ink/35 tracking-wide"
            >
              A selection of the banks and lenders on the LMG aggregation panel.
            </motion.p>
          </div>
        </div>
      </div>
    </section>
  );
}
